import { Chip, Spinner } from '@sjuni/ui';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Download, Inbox, Link2Off } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { FormError } from '@/components/FormError';
import { apiFetch } from '@/lib/api/client';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { TranslationChart } from '@/routes/dashboard/TranslationChart';
import type { DashboardStats } from '@/routes/dashboard/types';

/**
 * docs/05-ADMIN-DASHBOARD.md §5.1 — the landing screen.
 *
 * "Cards: total published news / programs / staff / documents · new contact
 * submissions (with a link) · content published this month · translation
 * completeness per content type · top 10 downloaded documents · scheduled
 * content in the next 7 days · recent activity feed · a 'broken links /
 * unmatched 404s' count linking to the redirect manager."
 *
 * §2 gates the screen on "authenticated" and the server gates each *section*
 * on the permission of the module it summarises — so a Faculty Admin's
 * dashboard is shorter rather than fuller of zeros. A section the server
 * withheld is `null` here and simply is not drawn: see `DashboardController`
 * for why null and 0 have to stay different.
 */
export function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const stats = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => apiFetch<{ data: DashboardStats }>('/admin/dashboard/stats'),
  });

  const data = stats.data?.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">
          {t('dashboard.greeting', { name: user?.name ?? '' })}
        </h1>
        <p className="mt-1 text-body-sm text-secondary">{t('dashboard.intro')}</p>

        {/*
          Who the reader is and what scopes them.

          Not in §5.1's card list, and it stays because §3 scopes a Faculty
          Admin by `users.faculty_id` and that enforcement is otherwise
          invisible: the only sign they are seeing one faculty's records rather
          than the university's is this line. A line rather than the two cards
          it used to be — the numbers below are what the screen is for now.
        */}
        <dl className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-caption">
          <dt className="sr-only">{t('dashboard.role')}</dt>
          {(user?.roles ?? []).map((role) => (
            <dd key={role}>
              <Chip tone="neutral">{t(`roles.${role}`, { defaultValue: role })}</Chip>
            </dd>
          ))}

          {user?.faculty?.name ? (
            <>
              <dt className="text-muted">{t('dashboard.faculty')}</dt>
              <dd>
                <Chip tone="navy">{user.faculty.name}</Chip>
              </dd>
            </>
          ) : null}
        </dl>
      </div>

      {stats.isError ? <FormError message={t('errors.generic')} /> : null}
      {stats.isPending ? <Spinner size="lg" label={t('common.loading')} /> : null}

      {data ? (
        <>
          {/* §5.1's count cards. Published figures, so every role sees them —
              they are what a visitor could count off the public site. */}
          <dl className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {(['news', 'programs', 'staff', 'documents'] as const).map((key) => (
              <StatTile
                key={key}
                label={t(`dashboard.counts.${key}`)}
                value={data.counts[key]}
                hint={t('dashboard.published')}
              />
            ))}

            <StatTile
              label={t('dashboard.thisMonth')}
              value={data.published_this_month}
              hint={t('dashboard.thisMonthHint')}
            />
          </dl>

          {/* The two cards that are a link rather than a number: both are work
              waiting to be done, and both belong to a module §3 restricts. */}
          {data.submissions !== null || data.not_found !== null ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {data.submissions !== null ? (
                <ActionCard
                  to="/submissions"
                  icon={<Inbox size={18} strokeWidth={1.75} aria-hidden="true" />}
                  label={t('dashboard.newSubmissions')}
                  value={data.submissions.new}
                  action={t('dashboard.openInbox')}
                />
              ) : null}

              {data.not_found !== null ? (
                <ActionCard
                  to="/redirects"
                  icon={<Link2Off size={18} strokeWidth={1.75} aria-hidden="true" />}
                  label={t('dashboard.unmatched', { days: data.not_found.days })}
                  value={data.not_found.count}
                  action={t('dashboard.openRedirects')}
                />
              ) : null}
            </div>
          ) : null}

          <TranslationChart rows={data.translation} />

          <div className="grid gap-4 lg:grid-cols-2">
            {data.scheduled !== null ? <Scheduled rows={data.scheduled} /> : null}
            {data.top_downloads !== null ? <TopDownloads rows={data.top_downloads} /> : null}
          </div>

          {data.activity !== null ? <RecentActivity rows={data.activity} /> : null}
        </>
      ) : null}
    </div>
  );
}

function StatTile({ label, value, hint }: { label: string; value: number; hint: string }) {
  return (
    <div className="rounded-card border border-subtle bg-surface p-5">
      <dt className="text-caption font-extrabold tracking-wide text-muted">{label}</dt>
      {/*
        The hint lives inside the `<dd>`, not beside it. A `<dl>` may hold only
        `<dt>`/`<dd>` pairs — optionally wrapped in a `<div>` — so a `<p>` in
        that wrapper is invalid, which `definition-list` fails on. It is also
        the honest structure: the hint qualifies the figure it sits under.
      */}
      <dd className="mt-1">
        {/* The figure is the point, so it is the biggest thing in the tile. */}
        <span className="block text-h3 font-extrabold tabular-nums text-primary">{value}</span>
        <span className="mt-0.5 block text-caption text-muted">{hint}</span>
      </dd>
    </div>
  );
}

/** §5.1's "with a link" — a count nobody can act on is just decoration. */
function ActionCard({
  to,
  icon,
  label,
  value,
  action,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  value: number;
  action: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-card border border-subtle bg-surface p-5">
      <div className="flex items-center gap-3">
        <span className="text-navy-600">{icon}</span>
        <span>
          <span className="block text-h4 font-extrabold tabular-nums text-primary">{value}</span>
          <span className="block text-caption text-muted">{label}</span>
        </span>
      </div>

      <Link
        to={to}
        className="inline-flex shrink-0 items-center gap-1.5 text-body-sm font-semibold text-navy-600 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
      >
        {action}
        <ArrowRight size={14} strokeWidth={1.75} aria-hidden="true" />
      </Link>
    </div>
  );
}

/** §5.1 — "scheduled content in the next 7 days". */
function Scheduled({ rows }: { rows: NonNullable<DashboardStats['scheduled']> }) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();

  return (
    <section aria-labelledby="scheduled-heading" className="rounded-card border border-subtle p-5">
      <h2 id="scheduled-heading" className="text-h4 font-extrabold text-primary">
        {t('dashboard.scheduled.title')}
      </h2>

      {rows.length === 0 ? (
        <p className="mt-3 text-body-sm text-secondary">{t('dashboard.scheduled.empty')}</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {rows.map((row) => (
            <li key={`${row.type}-${row.id}`} className="flex flex-wrap items-baseline gap-2">
              <Chip tone="neutral">{t(`activity.subjects.${row.type}`, row.type)}</Chip>
              <span className="min-w-0 flex-1 truncate text-body-sm text-primary">{row.title}</span>
              <span className="text-caption text-muted">
                {row.published_at ? new Date(row.published_at).toLocaleString(locale) : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** §5.1 — "top 10 downloaded documents". */
function TopDownloads({ rows }: { rows: NonNullable<DashboardStats['top_downloads']> }) {
  const { t } = useTranslation();

  return (
    <section aria-labelledby="downloads-heading" className="rounded-card border border-subtle p-5">
      <h2 id="downloads-heading" className="text-h4 font-extrabold text-primary">
        {t('dashboard.downloads.title')}
      </h2>

      {rows.length === 0 ? (
        <p className="mt-3 text-body-sm text-secondary">{t('dashboard.downloads.empty')}</p>
      ) : (
        <ol className="mt-3 space-y-2">
          {rows.map((row) => (
            <li key={row.id} className="flex items-baseline justify-between gap-3">
              <Link
                to={`/documents/${row.id}`}
                className="min-w-0 flex-1 truncate text-body-sm text-navy-600 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
              >
                {row.title}
              </Link>
              <span className="flex shrink-0 items-center gap-1 text-caption tabular-nums text-muted">
                <Download size={12} strokeWidth={1.75} aria-hidden="true" />
                {row.downloads}
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/** §5.1's "recent activity feed" — the head of `/activity`. */
function RecentActivity({ rows }: { rows: NonNullable<DashboardStats['activity']> }) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();

  return (
    <section aria-labelledby="activity-heading" className="rounded-card border border-subtle p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="activity-heading" className="text-h4 font-extrabold text-primary">
          {t('dashboard.activity.title')}
        </h2>

        <Link
          to="/activity"
          className="text-body-sm font-semibold text-navy-600 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
        >
          {t('dashboard.activity.all')}
        </Link>
      </div>

      {rows.length === 0 ? (
        <p className="mt-3 text-body-sm text-secondary">{t('dashboard.activity.empty')}</p>
      ) : (
        <ol className="mt-3 space-y-2">
          {rows.map((row) => (
            <li key={row.id} className="flex flex-wrap items-baseline gap-2 text-body-sm">
              {/* Same shape as the full feed — the action as a chip and the
                  actor on its own — because Georgian will not take a name
                  interpolated into a past-tense sentence. See `ActivityPage`. */}
              <Chip tone={row.event === 'deleted' ? 'gold' : 'neutral'}>
                {t(`activity.events.${row.event ?? 'updated'}`, row.event ?? '')}
              </Chip>
              <span className="text-secondary">
                {t(`activity.subjects.${row.subject.type ?? 'unknown'}`, row.subject.type ?? '')}
              </span>
              <span className="min-w-0 flex-1 truncate font-semibold text-primary">
                {row.subject.label ?? t('activity.subjectGone')}
              </span>
              <span className="text-caption text-muted">
                {row.causer?.name ?? t('activity.system')}
                {row.created_at ? ` · ${new Date(row.created_at).toLocaleDateString(locale)}` : ''}
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

export default DashboardPage;
